'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { format } from 'date-fns';
import { Calendar, Clock, Loader2, MapPin, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import { DateField } from '@kit/ui/date-field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@kit/ui/dialog';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import {
  addAuditionDateAction,
  deleteAuditionDateAction,
} from '../../server/actions/audition.actions';
import type {
  AuditionDateRow,
  AuditionWithDates,
} from '../../types/audition.types';

/**
 * Format error for display in toast
 */

interface DatesTabProps {
  audition: AuditionWithDates;
}

export function DatesTab({ audition }: DatesTabProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Form state for new date
  const [newDate, setNewDate] = useState({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    ageMin: '',
    ageMax: '',
    city: '',
    country: '',
    address: '',
  });

  const handleAddDate = async () => {
    if (!newDate.startDate) {
      toast.error(t('auditions:formTabs.dates.startDateRequired'));
      return;
    }

    setIsAdding(true);
    try {
      const result = await addAuditionDateAction(audition.id, {
        startDate: new Date(newDate.startDate),
        endDate: newDate.endDate ? new Date(newDate.endDate) : undefined,
        startTime: newDate.startTime || undefined,
        endTime: newDate.endTime || undefined,
        ageMin: newDate.ageMin ? parseInt(newDate.ageMin) : undefined,
        ageMax: newDate.ageMax ? parseInt(newDate.ageMax) : undefined,
        location: {
          city: newDate.city || undefined,
          country: newDate.country || undefined,
          address: newDate.address || undefined,
        },
      });

      if (result.success) {
        toast.success(t('auditions:formTabs.dates.dateAdded'));
        setIsDialogOpen(false);
        setNewDate({
          startDate: '',
          endDate: '',
          startTime: '',
          endTime: '',
          ageMin: '',
          ageMax: '',
          city: '',
          country: '',
          address: '',
        });
        router.refresh();
      } else {
        toast.error(result.error);
      }
    } catch {
      toast.error(t('auditions:formTabs.dates.unexpectedError'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteDate = async (dateId: string) => {
    if (!confirm(t('auditions:formTabs.dates.deleteConfirm'))) {
      return;
    }

    setDeletingId(dateId);
    try {
      const result = await deleteAuditionDateAction(dateId);
      if (result.success) {
        toast.success(t('auditions:formTabs.dates.dateDeleted'));
        router.refresh();
      } else {
        toast.error(result.error);
      }
    } catch {
      toast.error(t('auditions:formTabs.dates.unexpectedError'));
    } finally {
      setDeletingId(null);
    }
  };

  const dates = audition.audition_dates || [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>
              <Trans i18nKey="auditions:formTabs.dates.title" />
            </CardTitle>
            <CardDescription>
              <Trans i18nKey="auditions:formTabs.dates.description" />
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                <Trans i18nKey="auditions:formTabs.dates.addDate" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  <Trans i18nKey="auditions:formTabs.dates.addDateTitle" />
                </DialogTitle>
                <DialogDescription>
                  <Trans i18nKey="auditions:formTabs.dates.addDateDescription" />
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Date & Time */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">
                      <Trans i18nKey="auditions:formTabs.dates.startDateLabel" />
                    </Label>
                    <DateField
                      id="startDate"
                      value={newDate.startDate}
                      onChange={(value) =>
                        setNewDate({ ...newDate, startDate: value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endDate">
                      <Trans i18nKey="auditions:formTabs.dates.endDateLabel" />
                    </Label>
                    <DateField
                      id="endDate"
                      value={newDate.endDate}
                      onChange={(value) =>
                        setNewDate({ ...newDate, endDate: value })
                      }
                      min={newDate.startDate || undefined}
                      clearable
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="startTime">
                      <Trans i18nKey="auditions:formTabs.dates.startTimeLabel" />
                    </Label>
                    <Input
                      id="startTime"
                      type="time"
                      value={newDate.startTime}
                      onChange={(e) =>
                        setNewDate({ ...newDate, startTime: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endTime">
                      <Trans i18nKey="auditions:formTabs.dates.endTimeLabel" />
                    </Label>
                    <Input
                      id="endTime"
                      type="time"
                      value={newDate.endTime}
                      onChange={(e) =>
                        setNewDate({ ...newDate, endTime: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Age Range for this date */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="ageMin">
                      <Trans i18nKey="auditions:formTabs.dates.ageMinLabel" />
                    </Label>
                    <Input
                      id="ageMin"
                      type="number"
                      min={0}
                      max={100}
                      placeholder="e.g., 18"
                      value={newDate.ageMin}
                      onChange={(e) =>
                        setNewDate({ ...newDate, ageMin: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ageMax">
                      <Trans i18nKey="auditions:formTabs.dates.ageMaxLabel" />
                    </Label>
                    <Input
                      id="ageMax"
                      type="number"
                      min={0}
                      max={100}
                      placeholder="e.g., 25"
                      value={newDate.ageMax}
                      onChange={(e) =>
                        setNewDate({ ...newDate, ageMax: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label htmlFor="address">
                    <Trans i18nKey="auditions:formTabs.dates.addressLabel" />
                  </Label>
                  <Input
                    id="address"
                    placeholder="e.g., 123 Main Street"
                    value={newDate.address}
                    onChange={(e) =>
                      setNewDate({ ...newDate, address: e.target.value })
                    }
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="city">
                      <Trans i18nKey="auditions:formTabs.dates.cityLabel" />
                    </Label>
                    <Input
                      id="city"
                      placeholder="e.g., Paris"
                      value={newDate.city}
                      onChange={(e) =>
                        setNewDate({ ...newDate, city: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">
                      <Trans i18nKey="auditions:formTabs.dates.countryLabel" />
                    </Label>
                    <Input
                      id="country"
                      placeholder="e.g., France"
                      value={newDate.country}
                      onChange={(e) =>
                        setNewDate({ ...newDate, country: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={isAdding}
                >
                  <Trans i18nKey="common:buttons.cancel" />
                </Button>
                <Button onClick={handleAddDate} disabled={isAdding}>
                  {isAdding && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  <Trans i18nKey="auditions:formTabs.dates.addDate" />
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {dates.length === 0 ? (
          <div className="rounded-lg border border-dashed py-8 text-center">
            <Calendar className="text-muted-foreground mx-auto mb-2 h-8 w-8" />
            <p className="text-muted-foreground">
              <Trans i18nKey="auditions:formTabs.dates.emptyTitle" />
            </p>
            <p className="text-muted-foreground text-sm">
              <Trans i18nKey="auditions:formTabs.dates.emptyBody" />
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {dates.map((date) => (
              <DateCard
                key={date.id}
                date={date}
                onDelete={() => handleDeleteDate(date.id)}
                isDeleting={deletingId === date.id}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DateCard({
  date,
  onDelete,
  isDeleting,
}: {
  date: AuditionDateRow;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const { t } = useTranslation();
  const location = date.location as {
    address?: string;
    city?: string;
    country?: string;
  } | null;

  return (
    <div className="flex items-start justify-between rounded-lg border p-4">
      <div className="space-y-2">
        {/* Date */}
        <div className="flex items-center gap-2">
          <Calendar className="text-muted-foreground h-4 w-4" />
          <span className="font-medium">
            {format(new Date(date.start_date), 'EEEE, MMMM d, yyyy')}
          </span>
          {date.end_date && date.end_date !== date.start_date && (
            <span className="text-muted-foreground">
              - {format(new Date(date.end_date), 'MMMM d, yyyy')}
            </span>
          )}
        </div>

        {/* Time */}
        {(date.start_time || date.end_time) && (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4" />
            {date.start_time && <span>{date.start_time}</span>}
            {date.end_time && <span>- {date.end_time}</span>}
          </div>
        )}

        {/* Location */}
        {location &&
          (location.city || location.country || location.address) && (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4" />
              <span>
                {[location.address, location.city, location.country]
                  .filter(Boolean)
                  .join(', ')}
              </span>
            </div>
          )}

        {/* Age range for this date */}
        {(date.age_min || date.age_max) && (
          <div className="text-muted-foreground text-sm">
            {t('auditions:formTabs.dates.ageRange', {
              min: date.age_min || '?',
              max: date.age_max || '?',
            })}
          </div>
        )}
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={onDelete}
        disabled={isDeleting}
        className="text-destructive hover:text-destructive"
      >
        {isDeleting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Trash2 className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
}
